import { AuthState } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { getUserInfo } from "../api/mlbauth/UserInfoClient";
import { oktaAuth } from "../oktaAuth";
import { User } from "../types/User";
import { PermissionConstants } from "../constants/PermissionConstants";
import RouteConstants from "../constants/RouteConstants";
import { getDefaultRouteFromPermissions } from "../routeDefs";
import { datadogRum } from "@datadog/browser-rum";
import { isOAuthCallbackInProgress } from "../utils/OktaAuthUtil";

interface UserInfo extends User {
  accessToken: { accessToken: string; claims: { email: string }; expiresAt: number };
}

type UserContext = {
  userInfo: UserInfo;
  hasUserInfo: boolean;
  login?: (authState: AuthState) => void;
  logout: () => void;
  permissionError: boolean;
  loginFunction?: SetStateAction<void>;
  setPermissionError?: Dispatch<SetStateAction<boolean>>;
  setLoginFunction?: Dispatch<SetStateAction<void>>;
  getUserId?: () => number;
  hasPermission: (permission: PermissionConstants | string) => boolean;
  hasAnyPermission: (...permissions: (PermissionConstants | string)[]) => boolean;
  getDefaultRoute: () => RouteConstants;
};

const initialState: UserState = {
  userInfo: {
    accessToken: { accessToken: "", claims: { email: "" }, expiresAt: 0 },
    id: -1,
    trackviaAuthenticatorId: -1,
    username: "",
    firstName: "",
    lastName: "",
    fullName: "",
    roleId: null,
    role: "",
    roleDisplayName: "",
    orgId: -1,
    permissions: [],
  },
  loggedIn: false,
};

const UserContext = createContext<UserContext>({
  userInfo: initialState.userInfo,
  hasUserInfo: false,
  permissionError: false,
  logout: () => {
    console.log("logout");
  },
  hasPermission: (): boolean => false,
  hasAnyPermission: (): boolean => false,
  getDefaultRoute: () => RouteConstants.BASE,
});

interface UserState {
  userInfo: UserInfo;
  loggedIn: boolean;
}

type UserProviderProps = {
  children: React.ReactNode;
};

const UserProvider = ({ children }: UserProviderProps) => {
  const [userState, setUserState] = useState<UserState>(initialState);
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [loginFunction, setLoginFunction] = useState();
  const { authState } = useOktaAuth();
  const skipAutoLoginRef = useRef(false);

  const login = async (authState: AuthState) => {
    const userInfo = await getUserInfo();

    if (userInfo && authState.accessToken && authState.accessToken.claims.email) {
      setUserState({
        userInfo: {
          accessToken: { ...authState.accessToken, claims: { ...authState.accessToken.claims, email: "" } },
          ...userInfo,
          permissions: userInfo.permissions ? [...userInfo.permissions] : [],
        },
        loggedIn: true,
      });
    }
  };

  const logout = (): void => {
    // Do not wipe storage during the OAuth callback; that removes the PKCE codeVerifier.
    if (isOAuthCallbackInProgress()) {
      return;
    }
    skipAutoLoginRef.current = true;
    oktaAuth.tokenManager.clear();
    oktaAuth.transactionManager.clear();
    oktaAuth.authStateManager.updateAuthState();
    setUserState(initialState);
  };

  const getUserId = (): number => {
    return userState.userInfo.id;
  };

  const hasPermission = (permission: PermissionConstants | string): boolean => {
    return userState.userInfo.permissions.includes(permission);
  };

  const hasAnyPermission = (...permissions: (PermissionConstants | string)[]): boolean => {
    return permissions.some((permission) => hasPermission(permission));
  };

  const getDefaultRoute = () => {
    if (!userState.loggedIn) return RouteConstants.BASE;
    return getDefaultRouteFromPermissions(userState.userInfo.permissions);
  };

  useEffect(() => {
    if (skipAutoLoginRef.current) {
      if (authState && !authState.isAuthenticated) {
        skipAutoLoginRef.current = false;
      }
      return;
    }
    if (authState?.isAuthenticated) {
      // when the user becomes authenticated, call onLogin() to populate AuthContext's user info
      login({ accessToken: authState.accessToken });
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    if (window.MLBBest.envVariables.ENV !== "local" && !!userState) {
      datadogRum.setUser({
        id: userState.userInfo.id.toString(),
        email: userState.userInfo.username,
        name: `${userState.userInfo.lastName}, ${userState.userInfo.firstName}`,
      });
    } else {
      datadogRum.clearUser();
    }
  }, [userState]);

  return (
    <UserContext.Provider
      value={{
        userInfo: userState.userInfo,
        hasUserInfo: userState.loggedIn,
        login,
        logout,
        permissionError,
        setPermissionError,
        loginFunction,
        setLoginFunction,
        getUserId,
        hasPermission,
        hasAnyPermission,
        getDefaultRoute,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUser = (): UserContext => {
  const userContext = useContext<UserContext>(UserContext);
  if (userContext === undefined) {
    throw new Error(`useUser must be used within a UserProvider`);
  }
  return userContext;
};

export { oktaAuth } from "../oktaAuth";
export { UserContext, UserProvider, useUser, UserInfo };
